import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import DiscountTierAccordion from '../../DiscountTierAccordion';
import { getDiscountedPrice } from '../../DiscountTierAccordion/helpers';
import WysiwygField from '../../WysiwygField';
import { formatPrice } from '../../../helpers/commerce';

import './style.scss';

/**
 * Paid-product purchase card matching the "Product block" design.
 *
 * Reusable layout shell: renders the product image (with a fallback tile when
 * none is set), title, the green volume-discount accordion and the price. The
 * quantity counter + order button are passed in via `orderBar` so each caller
 * keeps its own, unchanged controls. The discounted price is derived from the
 * active tier (tiers are always a percentage) so it updates with the count.
 *
 * @param {object} props - Component props.
 * @param {string|null} props.productImage - Resolved product image URL.
 * @param {string} props.title - Product title.
 * @param {string} [props.description] - Optional product description (HTML).
 * @param {number} props.price - Original unit price.
 * @param {boolean} [props.isFree] - When true the card shows "Gratis" and omits
 *   the VAT note (free products carry no price or BTW line).
 * @param {Array} props.discountTiers - Volume discount tiers.
 * @param {number} props.quantity - Current quantity.
 * @param {React.ReactNode} props.orderBar - Quantity counter + order button.
 * @param {React.ReactNode} [props.variationSelect] - Optional variation select.
 * @returns {JSX.Element} The product card.
 */
const ProductBlock = ({
  productImage,
  title,
  description = '',
  price,
  isFree = false,
  discountTiers = [],
  quantity,
  orderBar,
  variationSelect = null,
}) => {
  const { t } = useTranslation();

  // Fall back to the placeholder tile when there is no image, or when the
  // image fails to load (e.g. a removed file leaving a stale derivative URL).
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(productImage) && !imageFailed;

  // Reset the failed flag when the image changes (e.g. variation switch) so a
  // valid new image isn't hidden by a previous one's load error.
  useEffect(() => {
    setImageFailed(false);
  }, [productImage]);

  const numericPrice = Number.parseFloat(price) || 0;
  const discountedPrice = getDiscountedPrice(
    numericPrice,
    discountTiers,
    quantity,
  );

  const priceLabel = (value) => t('cart.price_display', {
    price: formatPrice(value),
    interpolation: { escapeValue: true },
  });

  return (
    <div className="ProductBlock">
      <div className="ProductBlock__image">
        {showImage ? (
          <img
            src={productImage}
            alt={title}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="ProductBlock__image-fallback" aria-hidden="true">
            <span className="icon icon-shopping-cart" />
          </div>
        )}
      </div>

      <div className="ProductBlock__content">
        <h3 className="ProductBlock__title">{title}</h3>

        {description && (
          <WysiwygField
            className="ProductBlock__description"
            property="schema:text"
            html={description}
          />
        )}

        {discountTiers.length > 0 && (
          <DiscountTierAccordion
            tiers={discountTiers}
            activeQty={quantity}
            variant="product"
          />
        )}

        {variationSelect && (
          <div className="ProductBlock__variation">{variationSelect}</div>
        )}

        <div className="ProductBlock__orderRow">
          <div className="ProductBlock__order">{orderBar}</div>
          <div className="ProductBlock__price">
            {isFree ? (
              <span className="ProductBlock__price-current">
                {t('cart.free')}
              </span>
            ) : (
              <>
                <div className="ProductBlock__price-amounts">
                  {discountedPrice !== null && (
                    <span className="ProductBlock__price-original">
                      {priceLabel(numericPrice)}
                    </span>
                  )}
                  <span className="ProductBlock__price-current">
                    {priceLabel(discountedPrice ?? numericPrice)}
                  </span>
                </div>
                <span className="ProductBlock__price-note">
                  {t('cart.excl_btw')}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductBlock;
